// ─── COLOR PALETTES ───────────────────────────────────────────────────────────
// App-wide color data. Kept free of any logic so it can be imported anywhere
// without pulling in theme/state dependencies.

export const EVENT_COLORS = ["#f4a7b9","#b5d5f5","#b5e6c8","#f5d6a7","#d4b5f5","#f5b5b5"];
export const PRIORITY_COLORS = { urgent: "#e8464a", high: "#f0883e", medium: "#f5c518", low: "#7bc67e" };

// Fixed auto-assigned block colors for tasks/events with no explicit color set —
// deliberately NOT theme-toned, so a plain task/event reads the same pastel
// blue/purple no matter which app background theme is active.
export const DEFAULT_TASK_COLOR = "#aecbf2";
export const DEFAULT_EVENT_COLOR = "#cdb3ef";

// Ordered low→urgent for the task priority picker and list-card flag.
export const TASK_PRIORITIES = [
  { id: "low",    label: "Low"    },
  { id: "medium", label: "Medium" },
  { id: "high",   label: "High"   },
  { id: "urgent", label: "Urgent" },
];

// Auto-assigned palette — categories cycle through these, habits inherit from category.
// 12 soft, theme-matched pastels spread evenly around the colour wheel so every
// auto-assigned category reads as a DISTINCT hue — not just a different intensity
// of the same colour. Each entry sits ~30° from its neighbours (only ONE green,
// one teal, one lime, etc.) so no two cards ever look like the same colour.
export const AUTO_PALETTE = [
  "#f4a7b9", // Rose      ~345°
  "#f3917e", // Coral     ~10°
  "#ecb45a", // Amber     ~38°
  "#e3cf63", // Gold      ~52°
  "#a9cf72", // Lime      ~85°
  "#6fc78a", // Green     ~140°
  "#4fc2bb", // Teal      ~176°
  "#5bb6e4", // Sky       ~200°
  "#6d92e6", // Blue      ~222°
  "#8a7be0", // Indigo    ~250°
  "#b07fdc", // Purple    ~278°
  "#db84c4", // Magenta   ~318°
];
// Kept as aliases for anything that still references them.
export const GOAL_COLORS = AUTO_PALETTE;
export const HABIT_COLORS = AUTO_PALETTE;

export const NOTE_COLORS = [
  // White leads so a fresh note starts on a clean white canvas, then Black (Jade,
  // 2026-10-18: its text turns light, see utils/noteInk.js). The rest is a wide
  // pastel spectrum (all soft enough for dark text on top). The near-white
  // #fff7f9 was dropped (Jade, 2026-10-18): beside White it read as a second
  // white. Notes already on it keep it; it just has no swatch.
  "#ffffff","#111111","#fde4ec","#ffd1dc","#ffb3c6","#ff9eb5",
  "#ffc4b3","#ffd9c0","#ffe8cc","#fff0c2","#fdf2b8",
  "#f3edb0","#e6eeb0","#d4ecc0","#c0e8c8","#aee3cf",
  "#b4e8e0","#bfe3f0","#c2dcf5","#b8c9f5","#c8c2f7",
  "#d6c2f5","#e0c3fc","#ecc7f2","#f3cce8","#f0d9e6",
  "#e8d8e2","#ded6e0","#e2ddd5","#ececec",
];

// Dark note colours (Jade, 2026-10-28), shown under the pastels in the note
// background panel and in the Journal's text colour and highlight panels. Each
// is dark enough for isDarkNoteBg() (utils/noteInk.js), so a note on one gets
// light text. Ordered by hue like the pastels, then the near-greys.
export const NOTE_DARK_COLORS = [
  "#8a2a45","#5c1a2e","#5e1f52","#4a2470","#2f2a6b",
  "#1c2f66","#123f5c","#0f4c4a","#1f4d2c","#4a5216",
  "#6b4a12","#7a3a1c","#3b2f2f","#2b3440","#333333",
];

export const NOTE_GRADIENTS = [
  // 29 soft pastel gradients (no labels — chosen visually in the picker).
  { id:"g1",  value:"linear-gradient(135deg,#ffd6e7,#ffb3c6)" },
  { id:"g2",  value:"linear-gradient(135deg,#ffc4b3,#ff9eb5)" },
  { id:"g3",  value:"linear-gradient(135deg,#ffe1cf,#ffc4b3)" },
  { id:"g4",  value:"linear-gradient(135deg,#ffe8cc,#ffd1dc)" },
  { id:"g5",  value:"linear-gradient(135deg,#fff0c2,#ffd9c0)" },
  { id:"g6",  value:"linear-gradient(135deg,#fdf2b8,#fde68a)" },
  { id:"g7",  value:"linear-gradient(135deg,#f3edb0,#d4ecc0)" },
  { id:"g8",  value:"linear-gradient(135deg,#d4ecc0,#aee3cf)" },
  { id:"g9",  value:"linear-gradient(135deg,#c0e8c8,#b4e8e0)" },
  { id:"g10", value:"linear-gradient(135deg,#aee3cf,#bfe3f0)" },
  { id:"g11", value:"linear-gradient(135deg,#b4e8e0,#c2dcf5)" },
  { id:"g12", value:"linear-gradient(135deg,#bfe3f0,#b8c9f5)" },
  { id:"g13", value:"linear-gradient(135deg,#c2dcf5,#c8c2f7)" },
  { id:"g14", value:"linear-gradient(135deg,#b8c9f5,#d6c2f5)" },
  { id:"g15", value:"linear-gradient(135deg,#c8c2f7,#e0c3fc)" },
  { id:"g16", value:"linear-gradient(135deg,#d6c2f5,#ecc7f2)" },
  { id:"g17", value:"linear-gradient(135deg,#e0c3fc,#f3cce8)" },
  { id:"g18", value:"linear-gradient(135deg,#ecc7f2,#ffd1dc)" },
  { id:"g19", value:"linear-gradient(135deg,#f3cce8,#ffc4b3)" },
  { id:"g20", value:"linear-gradient(135deg,#ffd1dc,#c8c2f7)" },
  { id:"g21", value:"linear-gradient(135deg,#ffb3c6,#c2dcf5)" },
  { id:"g22", value:"linear-gradient(135deg,#ffd9c0,#aee3cf)" },
  { id:"g23", value:"linear-gradient(135deg,#fde68a,#aee3cf)" },
  { id:"g24", value:"linear-gradient(135deg,#c2dcf5,#d4ecc0)" },
  { id:"g25", value:"linear-gradient(135deg,#e0c3fc,#bfe3f0)" },
  { id:"g26", value:"linear-gradient(135deg,#ff9eb5,#ffd9c0)" },
  { id:"g27", value:"linear-gradient(135deg,#ffe8cc,#e0c3fc)" },
  { id:"g28", value:"linear-gradient(135deg,#fbcfe8,#c4b5fd,#bfdbfe)" },
  { id:"g29", value:"linear-gradient(135deg,#a7f3d0,#bfdbfe,#e0c3fc)" },
];

export const PETAL_COLORS = ["#f9a8c9","#fcd5e5","#f472b6","#fbcfe8","#fff","#fce4ee","#f0abcc","#ffe4f0"];
// Fixed pastel swatches for the block color picker — always the same
// regardless of the active app theme. Spread across distinct hues first
// (rose→coral→amber→gold→lime→green→teal→sky→blue→indigo→purple→magenta),
// then a second pass of the same hue families at a different tone/shade
// (e.g. two blues, two pinks, two greens) so there's still variety within
// a color family without every swatch looking the same.
export const PICKER_PRESETS = [
  "#f9c9d6", "#fbcabb", "#ffd9bb", "#ffe6b3", "#fbf1b5", "#e6f0b0",
  "#c4e9bd", "#bde9d6", "#bbe9e6", "#c0e0f2", "#b8d0f0", "#c6d3f5",
  "#d4c8f3", "#e3ccf2", "#f0cbef", "#f7cbe5",
];
export const CARD_ACCENTS = ["#f7a8c4","#f6b3a0","#f3cf86","#9ed8b8","#9ec4f5","#c3b0ef","#ef9ecb","#7fd0db","#f3b0b0","#bcd99a"];
